/**
 * Console product context utilities.
 *
 * Persists the current product context (ai-service / incubator) in sessionStorage
 * so the console sidebar menu can be dynamically filtered across navigations.
 *
 * Extracted from app.tsx to avoid circular imports — Umi's app.tsx is a special
 * runtime config file and must not be imported by page components.
 */

const CONSOLE_CTX_KEY = 'wischoicer_console_ctx';

export function setConsoleCtx(ctx: string) {
  if (typeof window === 'undefined') return;
  if (ctx) {
    sessionStorage.setItem(CONSOLE_CTX_KEY, ctx);
  } else {
    sessionStorage.removeItem(CONSOLE_CTX_KEY);
  }
}

export function getConsoleCtx(): string {
  if (typeof window === 'undefined') return '';
  return sessionStorage.getItem(CONSOLE_CTX_KEY) || '';
}

/**
 * 按当前路径推导产品上下文（WIS-369）。
 *
 * 侧栏原本只在 sessionStorage 写入 ctx 后才显示产品菜单，直接访问/新开标签页/书签打开
 * 内容孵化器等页面时 ctx 为空，会回退到底层 routes 菜单（出现 materialCollect 这类
 * 原始 key、分组错乱、缺「控制台」入口）。这里以路径为事实来源，content-workstation*
 * 恒为 incubator，AI 客服产品族恒为 ai-service，保证任何进入方式都落到正确的产品侧栏。
 * 对结尾斜杠做了归一化（生产构建 pathname 带 `/`）。
 */
export function deriveConsoleCtx(pathname: string): string {
  const p = String(pathname || '/').replace(/\?.*$/, '').replace(/#.*$/, '').replace(/\/+$/, '') || '/';
  if (p === '/content-workstation' || p.startsWith('/content-workstation/')) return 'incubator';
  if (
    p === '/ai-employee' ||
    p.startsWith('/ai-employee/') ||
    p === '/customer-service' ||
    p.startsWith('/customer-service/') ||
    p === '/knowledge' ||
    p.startsWith('/knowledge/')
  ) {
    return 'ai-service';
  }
  return '';
}

/**
 * 解析当前应使用的产品上下文：路径推导优先（事实来源），sessionStorage 仅作无路径信号时
 * （如 /advanced-tools 这类不属于明确产品族的工具页）的回退。
 */
export function currentConsoleCtx(pathname: string): string {
  return deriveConsoleCtx(pathname) || getConsoleCtx();
}

/**
 * 侧栏首项（path 为 /console 的顶部入口）显示文案（WIS-532）。
 *
 * 产品上下文内首项是「产品中心」入口（点击唤起产品切换抽屉，不跳回 /console），不再是
 * 「控制台」；/console 首页（无产品上下文）同样为「产品中心」。这里以显式白名单登记哪些
 * 产品上下文首项需要显示「控制台」，缺省（含 incubator / ai-service / 空 ctx 及任何未登记
 * 的 ctx）一律显示「产品中心」。未来确需显示「控制台」的新 ctx，请在 CONSOLE_LABEL_CTXS
 * 追加，不要再用 `ctx ? '控制台' : '产品中心'` 这种隐式判断。
 */
const CONSOLE_LABEL_CTXS: ReadonlyArray<string> = [
  // 暂无产品上下文首项需要显示「控制台」；新增请在此显式追加。
];

export function firstConsoleItemLabel(ctx: string): string {
  return CONSOLE_LABEL_CTXS.includes(ctx) ? '控制台' : '产品中心';
}

